import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type { components } from '@/api/schema';

export type TenantDto = components['schemas']['TenantDto'];
export type TenantSettingsDto = components['schemas']['TenantSettingsDto'];
export type UserDto = components['schemas']['UserDto'];
export type ChannelDto = components['schemas']['ChannelDto'];
export type MessageTemplateDto = components['schemas']['MessageTemplateDto'];
export type JdeVerifyResponse = components['schemas']['JdeVerifyResponse'];

/** Keys for one tenant's admin resources. Tenant ids come from the path, so they live under 'global'. */
export const tenantKeys = {
  list: () => queryKeys.tenants(),
  one: (id: string) => ['global', 'tenant', id] as const,
  part: (id: string, part: string) => ['global', 'tenant', id, part] as const,
};

const path = (id: string) => ({ params: { path: { id } } });

export function useTenants() {
  return useQuery({ queryKey: tenantKeys.list(), queryFn: async () => unwrap(await api.GET('/v1/tenants', { params: { query: { limit: 200 } } })) });
}

/** One tenant. `silent`: no error toast (e.g. the top-bar test-mode badge). */
export function useTenant(id: string, opts?: { silent?: boolean }) {
  return useQuery({
    queryKey: tenantKeys.one(id),
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}', path(id))),
    ...(opts?.silent ? { meta: { silent: true }, retry: false } : {}),
  });
}

export function useTenantSettings(id: string) {
  return useQuery({
    queryKey: tenantKeys.part(id, 'settings'),
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/settings', path(id))),
  });
}

export function useTenantUsers(id: string) {
  return useQuery({
    queryKey: tenantKeys.part(id, 'users'),
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/users', { params: { path: { id }, query: { limit: 200 } } })),
  });
}

export function useChannels(id: string) {
  return useQuery({ queryKey: tenantKeys.part(id, 'channels'), queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/channels', path(id))) });
}

export function useWhatsappTemplates(id: string, enabled: boolean) {
  return useQuery({
    queryKey: tenantKeys.part(id, 'wa-templates'),
    enabled,
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/channels/whatsapp/templates', path(id))),
  });
}

/** The tenant config cached from jde-napi (03 §3). Untyped in the OpenAPI document; shape owned by jde-napi. */
export function useJdeConfig(id: string, enabled = true) {
  return useQuery({
    queryKey: tenantKeys.part(id, 'jde-config'),
    enabled,
    retry: false,
    meta: { silent: true },
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/jde-config', path(id))) as unknown,
  });
}

export type JdeCredentialsDto = components['schemas']['JdeCredentialsDto'];

/** State of the agent API account (mode, client id, kid, public keys) — never secrets (ADR 0021). Platform admins only. */
export function useJdeCredentials(id: string, enabled = true) {
  return useQuery({
    queryKey: tenantKeys.part(id, 'jde-credentials'),
    enabled,
    meta: { silent: true },
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/jde-credentials', path(id))) as JdeCredentialsDto,
  });
}

/** Invalidates all cached parts of one tenant after a mutation, with a success toast. */
export function useTenantMutation<TVars, TResult>(
  id: string,
  fn: (vars: TVars) => Promise<TResult>,
  successMessage?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Callers map field errors onto their forms; don't double-report through the global toast.
    meta: { silent: true },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: tenantKeys.one(id) });
      await qc.invalidateQueries({ queryKey: tenantKeys.list() });
      if (successMessage) toast.success(successMessage);
    },
  });
}
